import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus } from "lucide-react";
import { FAQ } from "@/constants/testIds";

const FAQS = [
  {
    q: "What can I expect in our first session?",
    a: "Nothing more than showing up. There is no pressure to share everything at once — the first session is a chance to sit together, get a feel for the room, and see whether we&rsquo;re a good fit.",
  },
  {
    q: "How long does therapy take?",
    a: "Healing is nonlinear, and deeply personal. Some concerns settle within a few months; others ask for a longer, quieter kind of listening. What matters most is that we move at your pace — the work unfolds when it&rsquo;s ready, not on a schedule.",
  },
  {
    q: "Is this confidential?",
    a: "Yes — absolutely. What is spoken about in the room stays in the room. Confidentiality is not just a rule here; it&rsquo;s the container that makes real work possible. In the rare situations where safety is a concern, we would talk about it together first.",
  },
  {
    q: "Do you work online or in person?",
    a: "Both. Sessions are held in-person in Panjim, Goa, and online sessions are available for those who need the flexibility. We&rsquo;ll figure out what fits your life during our first conversation.",
  },
];

function Item({ item, i, open, setOpen }) {
  const isOpen = open === i;
  return (
    <div
      className="border-b border-border"
      data-testid={FAQ.item(i)}
    >
      <button
        onClick={() => setOpen(isOpen ? null : i)}
        data-testid={FAQ.trigger(i)}
        className="w-full flex items-start justify-between gap-6 py-8 md:py-10 text-left group"
      >
        <span className="font-serif text-xl md:text-3xl text-foreground leading-snug group-hover:text-primary transition-colors">
          {item.q}
        </span>
        <span
          className={`shrink-0 w-11 h-11 md:w-14 md:h-14 rounded-full border border-border flex items-center justify-center text-foreground transition-all duration-500 ${
            isOpen ? "bg-primary text-primary-foreground border-primary rotate-180" : "bg-transparent"
          }`}
        >
          {isOpen ? <Minus size={18} /> : <Plus size={18} />}
        </span>
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            key="content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden"
          >
            <div className="pb-10 pr-14 md:pr-24 max-w-2xl">
              <p
                className="text-base md:text-lg leading-relaxed text-foreground/75"
                dangerouslySetInnerHTML={{ __html: item.a }}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function FaqSection() {
  const [open, setOpen] = useState(0);
  return (
    <section
      id="faq"
      data-testid={FAQ.section}
      className="relative py-28 md:py-40 bg-muted/40"
    >
      <div className="mx-auto max-w-6xl px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-3xl mb-12 md:mb-16"
        >
          <span className="text-xs uppercase tracking-[0.3em] text-muted-foreground">
            (04) &nbsp; Common Questions
          </span>
          <h2 className="mt-6 font-serif text-4xl md:text-6xl leading-[1.02] text-foreground">
            You&rsquo;re allowed to <em className="text-primary">ask.</em>
          </h2>
        </motion.div>

        <div>
          {FAQS.map((item, i) => (
            <Item key={i} item={item} i={i} open={open} setOpen={setOpen} />
          ))}
        </div>
      </div>
    </section>
  );
}
